import { z } from 'zod'

import { apiDownload, apiRequest, apiUploadFormData } from '@/api/client'

const marketSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().nullable().optional(),
  event_date: z.string().nullable(),
  location: z.string().nullable().optional(),
  application_deadline: z.string().nullable().optional(),
  fee_standard: z.number(),
  fee_with_electricity: z.number(),
  bank_name: z.string().nullable().optional(),
  bank_account: z.string().nullable().optional(),
  payment_reference_hint: z.string().nullable().optional(),
  submit_whatsapp: z.string().nullable().optional(),
  is_published: z.boolean().optional(),
})

const applicationSchema = z.object({
  id: z.number(),
  market_id: z.number(),
  user_id: z.number().optional().nullable(),
  applicant_name: z.string(),
  business_name: z.string(),
  email: z.string(),
  cellphone: z.string(),
  items_for_sale: z.string().optional().nullable(),
  vendor_category: z
    .enum(['commercial', 'school_community', 'learner_classroom'])
    .optional()
    .nullable(),
  supervising_teacher: z.string().optional().nullable(),
  identity_number: z.string().optional().nullable(),
  stall_number: z.string().optional().nullable(),
  electricity_needed: z.boolean(),
  fee_amount: z.number().nullable().optional(),
  agreement_accepted: z.boolean().optional(),
  signatory_name: z.string().optional().nullable(),
  signed_on: z.string().nullable().optional(),
  status: z.enum(['submitted', 'approved', 'rejected']),
  admin_notes: z.string().nullable().optional(),
  content_approved: z.boolean().optional(),
  has_payment_proof: z.boolean().optional(),
  has_signature: z.boolean().optional(),
  has_paper_indemnity: z.boolean().optional(),
  has_indemnity: z.boolean().optional(),
  is_form_complete: z.boolean().optional(),
  can_approve: z.boolean().optional(),
  payment_proof_original_name: z.string().nullable().optional(),
  payment_proof_uploaded_at: z.string().nullable().optional(),
  paper_indemnity_original_name: z.string().nullable().optional(),
  paper_indemnity_uploaded_at: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  market_title: z.string().optional().nullable(),
  market_event_date: z.string().optional().nullable(),
  username: z.string().optional().nullable(),
  whatsapp_number: z.string().optional().nullable(),
  submit_whatsapp: z.string().optional().nullable(),
  bank_name: z.string().optional().nullable(),
  bank_account: z.string().optional().nullable(),
  payment_reference_hint: z.string().optional().nullable(),
  signature_data: z.string().optional().nullable(),
})

export type Market = z.infer<typeof marketSchema>

/** Friendly market-date label for dropdowns, e.g. "Saturday, 1 August 2026 — Oikos Market Day". */
export function formatMarketDateOption(market: Market): string {
  const raw = (market.event_date || '').slice(0, 10)
  if (!raw) return market.title
  const label = new Intl.DateTimeFormat('en-ZA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${raw}T12:00:00`))
  return `${label} — ${market.title}`
}
export type Application = z.infer<typeof applicationSchema>

type DocumentNameSource = Pick<
  Application,
  | 'id'
  | 'applicant_name'
  | 'business_name'
  | 'market_event_date'
  | 'payment_proof_original_name'
  | 'paper_indemnity_original_name'
>

/** Safe filename segment from a person's name. */
function slugPerson(value: string | null | undefined, fallback: string): string {
  const cleaned = (value || '')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60)
  return cleaned || fallback
}

/** Build download names like Oikos-Indemnity_Jane-Smith_2026-08-01.pdf */
export function applicationDocumentFilename(
  app: DocumentNameSource,
  kind: 'indemnity' | 'pop',
  extension = 'pdf',
): string {
  const person = slugPerson(
    app.applicant_name || app.business_name,
    `application-${app.id}`,
  )
  const datePart = (app.market_event_date || 'undated').slice(0, 10)
  const label = kind === 'indemnity' ? 'Oikos-Indemnity' : 'Oikos-POP'
  const ext = extension.replace(/^\./, '') || 'bin'
  return `${label}_${person}_${datePart}.${ext}`
}

function fileExtension(originalName: string | null | undefined, fallback = 'pdf'): string {
  const match = (originalName || '').match(/\.([a-zA-Z0-9]+)$/)
  return match?.[1]?.toLowerCase() || fallback
}

function popExtension(app: DocumentNameSource): string {
  return fileExtension(app.payment_proof_original_name)
}

function paperIndemnityExtension(app: DocumentNameSource): string {
  return fileExtension(app.paper_indemnity_original_name)
}

export function fetchMarkets() {
  return apiRequest('/api/v1/markets', (data) =>
    z.object({ markets: z.array(marketSchema) }).parse(data),
  )
}

const publicVendorSchema = z.object({
  id: z.number(),
  business_name: z.string(),
  applicant_name: z.string(),
  items_for_sale: z.string(),
})

const publicMarketVendorsSchema = z.object({
  market: z
    .object({
      id: z.number(),
      title: z.string(),
      event_date: z.string(),
    })
    .nullable(),
  vendors: z.array(publicVendorSchema),
})

export type PublicMarketVendor = z.infer<typeof publicVendorSchema>

/** Public approved vendors + products for a market day (YYYY-MM-DD). */
export function fetchPublicMarketVendors(eventDate: string) {
  const params = new URLSearchParams({ date: eventDate.slice(0, 10) })
  return apiRequest(`/api/v1/markets/vendors?${params}`, (data) => publicMarketVendorsSchema.parse(data), {
    authenticated: false,
  })
}

export function fetchMyApplications() {
  return apiRequest('/api/v1/applications/mine', (data) =>
    z.object({ applications: z.array(applicationSchema) }).parse(data),
  )
}

const vendorCategorySchema = z.object({
  value: z.enum(['commercial', 'school_community', 'learner_classroom']),
  label: z.string(),
})

const agreementTemplateSchema = z.object({
  version: z.string(),
  markdown: z.string(),
  vendor_categories: z.array(vendorCategorySchema),
})

export type VendorCategoryOption = z.infer<typeof vendorCategorySchema>
export type AgreementTemplate = z.infer<typeof agreementTemplateSchema>

/** Editable Markdown agreement used on the apply form and for PDF generation. */
export function fetchAgreementTemplate() {
  return apiRequest('/api/v1/applications/agreement-template', (data) =>
    agreementTemplateSchema.parse(data),
  )
}

export function fetchAdminApplications() {
  return apiRequest('/api/v1/applications/admin', (data) =>
    z.object({ applications: z.array(applicationSchema) }).parse(data),
  )
}

export function fetchApplication(id: number) {
  return apiRequest(`/api/v1/applications/${id}`, (data) =>
    z.object({ application: applicationSchema }).parse(data),
  )
}

export function submitApplication(payload: Record<string, unknown>) {
  return apiRequest('/api/v1/applications', (data) =>
    z.object({ application: applicationSchema }).parse(data),
    {
      method: 'POST',
      body: payload,
    },
  )
}

export type AdminCreateApplicationPayload = {
  user_id: number
  market_id: number
  applicant_name: string
  business_name: string
  email: string
  cellphone: string
  items_for_sale: string
  vendor_category: 'commercial' | 'school_community' | 'learner_classroom'
  supervising_teacher?: string | null
  identity_number?: string | null
  stall_number?: string | null
  electricity_needed: boolean
  signatory_name: string
  signed_on: string
}

/** Admin: create a market application for a vendor from a paper booking. */
export function createAdminApplication(payload: AdminCreateApplicationPayload) {
  return apiRequest('/api/v1/applications/admin', (data) =>
    z.object({ application: applicationSchema }).parse(data),
    {
      method: 'POST',
      body: payload,
    },
  )
}

export function updateApplicationStatus(
  id: number,
  payload: { status: string; admin_notes?: string },
) {
  return apiRequest(`/api/v1/applications/${id}/status`, (data) =>
    z.object({ application: applicationSchema }).parse(data),
    {
      method: 'PATCH',
      body: payload,
    },
  )
}

export type ApplicationUpdatePayload = {
  applicant_name: string
  business_name: string
  email: string
  cellphone: string
  items_for_sale: string
  vendor_category?: 'commercial' | 'school_community' | 'learner_classroom'
  supervising_teacher?: string | null
  identity_number?: string | null
  stall_number?: string | null
  electricity_needed: boolean
  fee_amount?: number | null
  signatory_name: string
  signed_on: string
  status: 'submitted' | 'approved' | 'rejected'
  admin_notes?: string | null
  content_approved: boolean
}

export type VendorApplicationUpdatePayload = {
  market_id: number
  applicant_name: string
  business_name: string
  email: string
  cellphone: string
  items_for_sale: string
  vendor_category: 'commercial' | 'school_community' | 'learner_classroom'
  supervising_teacher?: string | null
  identity_number?: string | null
  electricity_needed: boolean
  agreement_accepted: boolean
  signatory_name: string
  signed_on: string
  signature_data: string
}

export function updateApplication(id: number, payload: ApplicationUpdatePayload) {
  return apiRequest(`/api/v1/applications/${id}`, (data) =>
    z.object({ application: applicationSchema }).parse(data),
    {
      method: 'PATCH',
      body: payload,
    },
  )
}

export function updateMyApplication(id: number, payload: VendorApplicationUpdatePayload) {
  return apiRequest(`/api/v1/applications/${id}`, (data) =>
    z.object({ application: applicationSchema }).parse(data),
    {
      method: 'PATCH',
      body: payload,
    },
  )
}

export function downloadApplicationPdf(app: DocumentNameSource) {
  const filename = applicationDocumentFilename(app, 'indemnity', 'pdf')
  return apiDownload(`/api/v1/applications/${app.id}/pdf`, filename)
}

/** Blank printable indemnity form for paper bookings (admin). */
export function downloadBlankAgreementPdf() {
  return apiDownload(
    '/api/v1/applications/agreement-template/pdf',
    'Oikos-Indemnity_Blank.pdf',
  )
}

export function uploadPaymentProof(id: number, file: File) {
  const formData = new FormData()
  formData.append('file', file)
  return apiUploadFormData(
    `/api/v1/applications/${id}/payment-proof`,
    formData,
    (data) => z.object({ application: applicationSchema }).parse(data),
  )
}

export function downloadPaymentProof(app: DocumentNameSource) {
  const filename = applicationDocumentFilename(app, 'pop', popExtension(app))
  return apiDownload(
    `/api/v1/applications/${app.id}/payment-proof`,
    filename,
    'application/pdf,image/*,*/*',
  )
}

export function uploadPaperIndemnity(id: number, file: File) {
  const formData = new FormData()
  formData.append('file', file)
  return apiUploadFormData(
    `/api/v1/applications/${id}/paper-indemnity`,
    formData,
    (data) => z.object({ application: applicationSchema }).parse(data),
  )
}

export function downloadPaperIndemnity(app: DocumentNameSource) {
  const filename = applicationDocumentFilename(app, 'indemnity', paperIndemnityExtension(app))
  return apiDownload(
    `/api/v1/applications/${app.id}/paper-indemnity`,
    filename,
    'application/pdf,image/*,*/*',
  )
}
